import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
const EMPTY_SLOT = 'emptySlot';
const PLANE_TOP = 'planeTop';
const PLANE_BOTTOM = 'planeBottom';
const COLS = 20;
const ROWS = 16;
const GAP = 20;
const BOX_SIZE = {
  width: 100,
  height: 30,
  depth: 150
};
const PLANE_OFFSET = 250;
const ALL_ROWS_DEPTH = ROWS * (BOX_SIZE.depth + GAP);
const ALL_COLS_WIDTH = COLS * (BOX_SIZE.depth + GAP);
const SPEED_NORMAL = 4;
const SPEED_FAST = 34;
const num = (min, max) => Math.random() * (max - min) + min;
const VERTEX_SHADER = `
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
}`;
const FRAGMENT_SHADER = `
uniform float r;
uniform float g;
uniform float b;
uniform float distanceZ;
uniform float distanceX;
uniform float pulse;
uniform float speed;
varying vec2 vUv;
void main(void) {
  vec2 position = abs(-1.0 + 2.0 * vUv);
  float edging = abs((pow(position.y, 5.0) + pow(position.x, 5.0)) / 2.0);
  float perc = (0.2 * pow(speed + 1.0, 2.0) + edging * 0.8) * distanceZ * distanceX;
  float red = r * perc + pulse;
  float green = g * perc + pulse;
  float blue = b * perc + pulse;
  gl_FragColor = vec4(red, green, blue, 1.0);
}`;
function drawBox(width, height, depth) {
  const colours = {
    // 暖色大杂烩：橙 / 橙红 / 正红 / 粉红 / 白 都有，按住时饱和度更高、更亮
    slow: {
      r: num(0.7, 1.0),
      g: num(0.2, 0.7),
      b: num(0.1, 0.5)
    },
    fast: {
      r: num(0.9, 1.0),
      g: num(0.1, 0.7),
      b: num(0.2, 0.6)
    }
  };
  const uniforms = {
    r: {
      value: colours.slow.r
    },
    g: {
      value: colours.slow.g
    },
    b: {
      value: colours.slow.b
    },
    distanceX: {
      value: 1.0
    },
    distanceZ: {
      value: 1.0
    },
    pulse: {
      value: 0
    },
    speed: {
      value: SPEED_NORMAL
    }
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER
  });
  const geometry = new THREE.BoxGeometry(width, height, depth);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.colours = colours;
  return mesh;
}
export const WarpGrid = ({
  width = '100%',
  height = '100%',
  className,
  style
}) => {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let sw = container.clientWidth || window.innerWidth;
    let sh = container.clientHeight || window.innerHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(100, sw / sh, 1, 10000);
    scene.add(camera);
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(sw, sh);
    container.appendChild(renderer.domElement);
    let isMouseDown = false;
    const mouse = {
      x: 0,
      y: 0
    };
    const camPos = {
      x: 0,
      y: 0,
      z: 10
    };
    let speed = SPEED_NORMAL;
    const boxes = {
      planeBottom: [],
      planeTop: []
    };
    const boxes1d = [];
    for (let j = 0; j < ROWS; j++) {
      boxes[PLANE_BOTTOM][j] = [];
      boxes[PLANE_TOP][j] = [];
      for (let i = 0; i < COLS; i++) {
        boxes[PLANE_BOTTOM][j][i] = EMPTY_SLOT;
        boxes[PLANE_TOP][j][i] = EMPTY_SLOT;
      }
    }
    const createBox = () => {
      const xi = Math.floor(Math.random() * COLS);
      const yi = Math.random() > 0.5 ? 1 : -1;
      const yai = yi === -1 ? PLANE_BOTTOM : PLANE_TOP;
      const zi = Math.floor(Math.random() * ROWS);
      const x = (xi - COLS / 2) * (BOX_SIZE.width + GAP);
      const y = yi * PLANE_OFFSET;
      const z = zi * (BOX_SIZE.depth + GAP);
      if (boxes[yai][zi][xi] === EMPTY_SLOT) {
        const box = drawBox(BOX_SIZE.width, BOX_SIZE.height, BOX_SIZE.depth);
        box.position.y = y;
        box.isWarping = false;
        box.offset = {
          x,
          z: 0
        };
        box.posZ = z;
        boxes[yai][zi][xi] = box;
        boxes1d.push(box);
        scene.add(box);
      }
    };
    for (let i = 0; i < ROWS * COLS; i++) {
      createBox();
    }
    const handleResize = () => {
      sw = container.clientWidth || window.innerWidth;
      sh = container.clientHeight || window.innerHeight;
      camera.aspect = sw / sh;
      camera.updateProjectionMatrix();
      renderer.setSize(sw, sh);
    };
    const handleDown = () => {
      isMouseDown = true;
    };
    const handleMove = e => {
      let ev = e;
      const te = e;
      if (te.changedTouches && te.changedTouches[0]) {
        ev = te.changedTouches[0];
      }
      mouse.x = ev.clientX / sw * 2 - 1;
      mouse.y = -(ev.clientY / sh) * 2 + 1;
    };
    const handleUp = () => {
      isMouseDown = false;
    };
    const addListeners = (events, handler) => {
      events.forEach(ev => window.addEventListener(ev, handler));
    };
    const removeListeners = (events, handler) => {
      events.forEach(ev => window.removeEventListener(ev, handler));
    };
    addListeners(['resize'], handleResize);
    addListeners(['mousedown', 'touchstart'], handleDown);
    addListeners(['mousemove', 'touchmove'], handleMove);
    addListeners(['mouseup', 'touchend'], handleUp);
    const move = (x, y, z) => {
      const box = boxes[y][z][x];
      if (box === EMPTY_SLOT) return;
      box.position.x = box.offset.x;
      box.position.z = box.offset.z + box.posZ;
      if (box.position.z > 0) {
        box.posZ -= ALL_ROWS_DEPTH;
      }
      if (!box.isWarping && Math.random() > 0.999) {
        const dir = Math.floor(Math.random() * 5);
        let xn = x;
        let zn = z;
        let yn = y;
        switch (dir) {
          case 0:
            xn += 1;
            break;
          case 1:
            xn -= 1;
            break;
          case 2:
            zn += 1;
            break;
          case 3:
            zn -= 1;
            break;
          default:
            yn = y === PLANE_TOP ? PLANE_BOTTOM : PLANE_TOP;
            break;
        }
        if (boxes[yn] && boxes[yn][zn] && boxes[yn][zn][xn] === EMPTY_SLOT) {
          boxes[y][z][x] = EMPTY_SLOT;
          box.isWarping = true;
          boxes[yn][zn][xn] = box;
          box.isWarping = false;
        }
      }
    };
    let rafId = 0;
    const renderFrame = () => {
      speed -= (speed - (isMouseDown ? SPEED_FAST : SPEED_NORMAL)) * 0.05;
      for (let b = 0; b < boxes1d.length; b++) {
        const box = boxes1d[b];
        box.posZ += speed;
        const distanceZ = 1 - ((ALL_ROWS_DEPTH - box.posZ) / ALL_ROWS_DEPTH - 1);
        box.material.uniforms.distanceZ.value = distanceZ;
        const distanceX = 1 - Math.abs(box.position.x) / (ALL_COLS_WIDTH / 3);
        box.material.uniforms.distanceX.value = distanceX;
        const colour = isMouseDown ? box.colours.fast : box.colours.slow;
        box.material.uniforms.r.value -= (box.material.uniforms.r.value - colour.r) * 0.1;
        box.material.uniforms.g.value -= (box.material.uniforms.g.value - colour.g) * 0.1;
        box.material.uniforms.b.value -= (box.material.uniforms.b.value - colour.b) * 0.1;
        const currentSpeed = (speed - SPEED_NORMAL) / (SPEED_FAST - SPEED_NORMAL);
        box.material.uniforms.speed.value = currentSpeed;
        if (Math.random() > 0.99995 - currentSpeed * 0.005) {
          box.material.uniforms.pulse.value = 1;
        }
        box.material.uniforms.pulse.value -= box.material.uniforms.pulse.value * 0.1 / (currentSpeed + 1);
      }
      for (let j = 0; j < ROWS; j++) {
        for (let i = 0; i < COLS; i++) {
          move(i, PLANE_BOTTOM, j);
          move(i, PLANE_TOP, j);
        }
      }
      camPos.x -= (camPos.x - mouse.x * 400) * 0.02;
      camPos.y -= (camPos.y - mouse.y * 150) * 0.05;
      camPos.z = -100;
      camera.position.set(camPos.x, camPos.y, camPos.z);
      camera.rotation.y = camPos.x / -1000;
      camera.rotation.x = camPos.y / 1000;
      camera.rotation.z = (camPos.x - mouse.x * 400) / 2000;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(renderFrame);
    };
    rafId = requestAnimationFrame(renderFrame);
    return () => {
      cancelAnimationFrame(rafId);
      removeListeners(['resize'], handleResize);
      removeListeners(['mousedown', 'touchstart'], handleDown);
      removeListeners(['mousemove', 'touchmove'], handleMove);
      removeListeners(['mouseup', 'touchend'], handleUp);
      boxes1d.forEach(b => {
        b.geometry.dispose();
        b.material.dispose();
      });
      renderer.dispose();
      if (renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);
  const sizeStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: className,
    style: {
      overflow: 'hidden',
      background: 'black',
      ...sizeStyle,
      ...style
    }
  });
};